import {
  ErrorMessage,
  Loading,
} from "@whitespace/gatsby-theme-smorgasbord/src/components";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";

import useTrimma from "../hooks/useTrimma";

import * as defaultStyles from "./ApartmentDeclaration.module.css";

ApartmentDeclaration.propTypes = {
  styles: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  apartment: PropTypes.shape({
    id: PropTypes.string,
    address: PropTypes.shape({
      street: PropTypes.string,
      zipcode: PropTypes.number,
      city: PropTypes.string,
      country: PropTypes.string,
    }),
    name: PropTypes.string,
  }).isRequired,
};

function transformItems(items) {
  return items
    .sort((a, b) => {
      return parseInt(a.sortOrder) - parseInt(b.sortOrder);
    })
    .reduce((acc, item) => {
      const data = {
        name: item.property.name,
        value: item.value,
      };

      const groupKey =
        item.property.secondaryPropertyGroupName ||
        item.property.propertyGroupName;

      if (acc[groupKey]) {
        acc[groupKey].push(data);
      } else {
        acc[groupKey] = [data];
      }

      return acc;
    }, {});
}

export default function ApartmentDeclaration({
  styles = defaultStyles,
  className,
  apartment: { id: apartmentID, address: apartmentAddress },
  ...restProps
}) {
  const { t } = useTranslation();

  const {
    loading,
    error,
    data: { data: content } = {},
  } = useTrimma(`/apartment-declaration/${apartmentID}`, {}, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorMessage error={error.message} severity="error" />;
  }

  if (!content) {
    return <div>No apartment declaration available!</div>;
  }

  const { Lägenhetspoäng: apartmentData, ...groups } = transformItems(
    content.propertyValues.propertyValue,
  );

  return (
    <div className={clsx(styles.component, className)} {...restProps}>
      <h2 className={clsx(styles.headline)}>{t("aboutApartment")}</h2>

      <dl className={clsx(styles.list)}>
        {content.id && (
          <div className={clsx(styles.item)}>
            <dt className={clsx(styles.label)}>{t("apartmentNumber")}</dt>
            <dd className={clsx(styles.data)}>{content.id}</dd>
          </div>
        )}
        {apartmentData &&
          apartmentData.map(({ name, value }, index) => (
            <div className={clsx(styles.item)} key={index}>
              <dt className={clsx(styles.label)}>{name}</dt>
              <dd className={clsx(styles.data)}>{value}</dd>
            </div>
          ))}
        <div className={clsx(styles.item)}>
          <dt className={clsx(styles.label)}>{t("address")}</dt>
          <dd className={clsx(styles.data)}>
            {apartmentAddress.street}, {apartmentAddress.zipcode}{" "}
            {apartmentAddress.city}
          </dd>
        </div>
        {content.targetRent && (
          <div className={clsx(styles.item)}>
            <dt className={clsx(styles.label)}>{t("targetRent")}</dt>
            <dd className={clsx(styles.data)}>
              {content.targetRent + " " + t("currencyPerYear")}
            </dd>
          </div>
        )}
      </dl>

      {groups &&
        Object.keys(groups).map((groupName, index) => (
          <table className={clsx(styles.table)} key={index}>
            <caption className={clsx(styles.caption)}>{groupName}</caption>
            <tbody>
              {groups[groupName].map(({ name, value }, index) => (
                <tr key={index}>
                  <td>{name}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </div>
  );
}
